import { FC, useContext, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Label, Button, Preloader, Paragraph } from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';

import { DiscInfo } from './DiscInfo';
import {
  IHostsByIdsRequest,
  IHostsByIdsResponse,
  ISidebarProps,
} from './interfaces';
import { GET_HOST_BY_ID } from './schema';
import s from './Sidebar.module.scss';
import { useApplyNewConfig } from './useApplyNewConfig';
import { getLabels } from './utils';

export const Sidebar: FC<ISidebarProps> = ({ hostId, onClose }) => {
  const { t } = useContext(LocalizationContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const isPCStatusAvailable = useIsFeatureAvailable('pc_status');

  const { data, loading } = useQuery<IHostsByIdsResponse, IHostsByIdsRequest>(
    GET_HOST_BY_ID,
    {
      fetchPolicy: 'no-cache',
      variables: {
        ids: [hostId],
      },
      skip: !isPCStatusAvailable,
    }
  );

  const { loading: isApplying, applyNewConfig } = useApplyNewConfig(hostId);
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);

  const {
    info,
    counters,
    alias,
    comment,
    mac_addr,
    ip_addr,
    device_has_changed,
  } = data?.hostsByIds?.[0] ?? {};
  const { disk_status, active_window } = counters ?? {};
  const labels = getLabels(data?.hostsByIds?.[0]);

  const noDataPlaceholder = t('noInfo');

  const properties = [
    {
      title: t('host.cpu'),
      data: info?.processor,
    },
    {
      title: t('host.ram'),
      data: info?.ram,
    },
    {
      title: t('host.gpu'),
      data: info?.video,
    },
  ];

  return (
    <FeatureHOC alias="pc_status">
      <div className={s.sidebar} data-name="sidebar">
        <Button
          onClick={onClose}
          variant="ghost"
          iconName="CloseIcon"
          onlyIcon
          className={s.close}
        />
        <Preloader loading={loading}>
          <div className={s.block}>
            <div className={s.alias}>{alias}</div>
            <div className={s.labels}>
              {labels.map(({ color, locale }) => (
                <Label
                  color={color}
                  text={t(locale)}
                  key={`sidebar-host-${alias}-status-${locale}`}
                  rounded
                />
              ))}
            </div>
          </div>
          {device_has_changed && isNotificationVisible && (
            <div className={s.notification}>
              {t('host.configChanged')}
              {permissions?.has('host_manage') && (
                <Button
                  variant="outline"
                  color="orange"
                  fullWidth
                  size="s"
                  disabled={isApplying}
                  onClick={async () => {
                    await applyNewConfig();
                    setIsNotificationVisible(false);
                  }}
                >
                  {t('host.configChanged.apply')}
                </Button>
              )}
            </div>
          )}
          <FeatureHOC alias="pc_status">
            {properties.map((prop) => (
              <div key={`sidebar-properties-${prop.title}`} className={s.block}>
                <span className={s.title}>{prop.title}</span>
                <span className={cn(!prop.data && s.placeholder)}>
                  {prop.data ?? noDataPlaceholder}
                </span>
              </div>
            ))}
          </FeatureHOC>
          <FeatureHOC alias="pc_disk_usage">
            <div className={s.block}>
              <span className={s.title}>{t('host.storages')}</span>
              {disk_status ? (
                disk_status.map((disk) => (
                  <DiscInfo
                    key={`sidebar-disk-info-${disk?.letter}`}
                    letter={disk?.letter}
                    total={disk?.total}
                    used={disk?.used}
                  />
                ))
              ) : (
                <span className={s.placeholder}>{noDataPlaceholder}</span>
              )}
            </div>
          </FeatureHOC>
          <div className={s.block}>
            <span className={s.title}>{t('host.network')}</span>
            {mac_addr || ip_addr ? (
              <>
                {ip_addr && <span>{ip_addr}</span>}
                {mac_addr && <span>{mac_addr?.toUpperCase()}</span>}
              </>
            ) : (
              <span className={s.placeholder}>{noDataPlaceholder}</span>
            )}
          </div>
          <FeatureHOC alias="active_window">
            <div className={s.block}>
              <span className={s.title}>{t('host.application.active')}</span>
              {active_window ? (
                <Paragraph size="m">{active_window}</Paragraph>
              ) : (
                <span className={s.placeholder}>{noDataPlaceholder}</span>
              )}
            </div>
          </FeatureHOC>
          <FeatureHOC alias="pc_comments">
            <div className={s.block}>
              <span className={s.title}>{t('host.comments')}</span>
              <span className={cn(!comment && s.placeholder)}>
                {comment ?? noDataPlaceholder}
              </span>
            </div>
          </FeatureHOC>
        </Preloader>
      </div>
    </FeatureHOC>
  );
};
